/**
 * TASK-736 [S4-I1] — Users & Access hub
 * Clean tile + modal UX aligned with Finance hub / adminV2Theme.
 * No full-page profile scroll — member details open in dialogs.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import getApiBase from "../../../components/getApiBase";
import { adminV2Theme as t } from "../theme";

function SummaryChip({ label, value }) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: `${t.radius.sm}px`,
        background: t.colors.surface,
        border: `1px solid ${t.colors.border}`,
        minWidth: 120,
      }}
    >
      <Typography sx={{ fontSize: 11, color: t.colors.textMuted, fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: t.colors.text }}>
        {value}
      </Typography>
    </Box>
  );
}

function MemberTile({ member, onClick }) {
  const name = `${member.first_name || ""} ${member.last_name || ""}`.trim() || "—";
  const role = (member.role_types && member.role_types[0]) || "admin";
  const isTest = Boolean(member.is_test_account);
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        textAlign: "left",
        cursor: "pointer",
        background: t.colors.surface,
        border: `1px solid ${t.colors.border}`,
        borderRadius: `${t.radius.md}px`,
        boxShadow: t.shadow.card,
        p: 2,
        minHeight: 120,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        transition: "border-color 0.15s, transform 0.15s",
        fontFamily: t.font.family,
        opacity: isTest ? 0.92 : 1,
        "&:hover": {
          borderColor: t.colors.primary,
          transform: "translateY(-1px)",
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, color: t.colors.text }}>{name}</Typography>
        <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
          {isTest && (
            <Chip
              label="Test"
              size="small"
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 600,
                background: t.colors.warningSoft,
                color: t.colors.text,
              }}
            />
          )}
          <Chip
            label={role}
            size="small"
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 600,
              background: t.colors.accentSoft,
              color: t.colors.text,
            }}
          />
        </Box>
      </Box>
      <Typography sx={{ fontSize: 13, color: t.colors.textMuted }}>{member.email}</Typography>
      <Typography sx={{ fontSize: 13, color: t.colors.textMuted }}>
        {member.phone_number || "No phone"}
      </Typography>
      <Typography sx={{ fontSize: 12, color: t.colors.primary, fontWeight: 600, mt: "auto" }}>
        Open →
      </Typography>
    </Box>
  );
}

function SectionTile({ title, status, soon, onClick }) {
  const isWire = status === "Wireframe";
  return (
    <Box
      component="button"
      type="button"
      onClick={soon ? undefined : onClick}
      disabled={soon}
      sx={{
        textAlign: "left",
        cursor: soon ? "default" : "pointer",
        background: t.colors.surface,
        border: `1px solid ${t.colors.border}`,
        borderRadius: `${t.radius.md}px`,
        p: 1.75,
        minHeight: 88,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        fontFamily: t.font.family,
        opacity: soon ? 0.7 : 1,
        "&:hover": soon ? {} : { borderColor: t.colors.primary },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{title}</Typography>
        <Chip
          label={status}
          size="small"
          sx={{
            height: 20,
            fontSize: 10,
            fontWeight: 600,
            background: isWire ? t.colors.navSoonBg : t.colors.primarySoft,
            color: t.colors.text,
          }}
        />
      </Box>
      {!soon && (
        <Typography sx={{ fontSize: 12, color: t.colors.primary, fontWeight: 600, mt: "auto" }}>
          {isWire ? "Preview →" : "Edit →"}
        </Typography>
      )}
    </Box>
  );
}

function ContactDialog({ open, onClose, member, canEdit, onSaved }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !member) return;
    setFirstName(member.first_name || "");
    setLastName(member.last_name || "");
    setPhone(member.phone_number || "");
    setMessage("");
    setError("");
  }, [open, member]);

  const save = async () => {
    if (!member || !canEdit) return;
    setSaving(true);
    setMessage("");
    setError("");
    try {
      const res = await fetch(
        `${getApiBase()}/api/admin/v2/users/${member.user_id}/contact`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            first_name: firstName,
            last_name: lastName,
            phone_number: phone,
          }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Save failed (${res.status})`);
      setMessage(data.message || "Saved");
      onSaved({
        ...member,
        first_name: data.first_name,
        last_name: data.last_name,
        phone_number: data.phone_number,
        email: data.email,
      });
    } catch (e) {
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!member) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={typeof window !== "undefined" && window.innerWidth < 600}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: `${t.radius.lg}px` },
          boxShadow: t.shadow.modal,
          fontFamily: t.font.family,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, fontFamily: t.font.family }}>Contact</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        {message && <Alert severity="success">{message}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          disabled={!canEdit || saving}
          fullWidth
          size="small"
        />
        <TextField
          label="Last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          disabled={!canEdit || saving}
          fullWidth
          size="small"
        />
        <TextField
          label="Login email"
          value={member.email || ""}
          disabled
          fullWidth
          size="small"
          helperText="Login credential — not editable here"
        />
        <TextField
          label="Phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={!canEdit || saving}
          fullWidth
          size="small"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Close
        </Button>
        {canEdit && (
          <Button
            variant="contained"
            onClick={save}
            disabled={saving}
            sx={{
              textTransform: "none",
              background: t.colors.primary,
              "&:hover": { background: t.colors.primaryHover },
            }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function WireframeBanner() {
  return (
    <Alert
      severity="info"
      sx={{
        mb: 2,
        background: t.colors.accentSoft,
        color: t.colors.text,
        border: `1px solid ${t.colors.border}`,
        "& .MuiAlert-icon": { color: t.colors.accent },
      }}
    >
      Wireframe for team review — layout only; save is disabled until this section goes live.
    </Alert>
  );
}

function Field({ label, value, multiline }) {
  return (
    <TextField
      label={label}
      value={value}
      fullWidth
      size="small"
      disabled
      multiline={Boolean(multiline)}
      minRows={multiline ? 2 : undefined}
    />
  );
}

function AccessRow({ label, invited }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        py: 1,
        borderBottom: `1px solid ${t.colors.border}`,
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{label}</Typography>
      <Chip
        label={invited ? "Invited (sample)" : "Not invited (sample)"}
        size="small"
        sx={{
          height: 22,
          fontSize: 11,
          fontWeight: 600,
          background: invited ? t.colors.successSoft : t.colors.warningSoft,
          color: t.colors.text,
        }}
      />
    </Box>
  );
}

function TaskRow({ keyId, summary, status }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1,
        py: 1.25,
        borderBottom: `1px solid ${t.colors.border}`,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: t.colors.accent }}>
          {keyId}
        </Typography>
        <Typography sx={{ fontSize: 13, color: t.colors.textMuted }}>{summary}</Typography>
      </Box>
      <Chip
        label={status}
        size="small"
        sx={{ height: 22, fontSize: 11, background: t.colors.navSoonBg }}
      />
    </Box>
  );
}

/** Demo-ready wireframes (I2–I5) — no persistence. */
function WireframeDialog({ open, onClose, section, member }) {
  const name = member
    ? `${member.first_name || ""} ${member.last_name || ""}`.trim()
    : "Team member";

  const body = (() => {
    switch (section) {
      case "raci":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Field label="Job title" value="Platform developer (sample)" />
            <Field label="RACI" value="R — Responsible (sample)" />
            <Field
              label="Current assignment"
              value="Admin v2 Users & Access wireframes (sample)"
              multiline
            />
          </Box>
        );
      case "timeline":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Field label="Started on" value="2025-03-01 (sample)" />
            <Field label="Tenure" value="~1 year (sample)" />
            <Field label="Notes" value="Joined during admin rebuild (sample)" multiline />
          </Box>
        );
      case "messaging":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Field label="Discord handle" value="@teammate (sample)" />
            <Field label="Preferred contact" value="Discord → phone → email (sample)" />
          </Box>
        );
      case "platform_access":
        return (
          <Box>
            <Typography sx={{ fontSize: 13, color: t.colors.textMuted, mb: 1 }}>
              Invite identity: {member?.email || "—"}
            </Typography>
            <AccessRow label="Supabase org" invited={false} />
            <AccessRow label="GitHub org" invited={true} />
            <AccessRow label="Expo project" invited={false} />
            <AccessRow label="Jira" invited={true} />
            <Button
              disabled
              fullWidth
              variant="outlined"
              sx={{ mt: 2, textTransform: "none" }}
            >
              Copy emails for invites (coming later)
            </Button>
          </Box>
        );
      case "projects_tasks":
        return (
          <Box>
            <TaskRow
              keyId="TASK-736"
              summary="Users & Access — team roster (sample)"
              status="In Progress"
            />
            <TaskRow
              keyId="TASK-680"
              summary="Admin Dashboard Program (sample)"
              status="To Do"
            />
            <TaskRow
              keyId="TASK-695"
              summary="Platform vault (sample)"
              status="In Progress"
            />
          </Box>
        );
      case "permission_groups":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography sx={{ fontSize: 13, color: t.colors.textMuted }}>
              Effective groups for {name} (sample)
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {["super_admin", "operations", "support"].map((g) => (
                <Chip
                  key={g}
                  label={g}
                  sx={{ background: t.colors.primarySoft, fontWeight: 600 }}
                />
              ))}
            </Box>
            <Field
              label="Assign groups"
              value="Group picker UI — wireframe only"
            />
          </Box>
        );
      case "audit":
        return (
          <Box>
            {[
              { when: "2026-08-12 08:20", action: "team_contact.update", who: "platform admin" },
              { when: "2026-08-10 14:02", action: "profile.view", who: "platform admin" },
              { when: "2026-07-28 11:40", action: "access.flag (sample)", who: "system" },
            ].map((row) => (
              <Box
                key={`${row.when}-${row.action}`}
                sx={{
                  py: 1.25,
                  borderBottom: `1px solid ${t.colors.border}`,
                }}
              >
                <Typography sx={{ fontSize: 12, color: t.colors.textMuted }}>{row.when}</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{row.action}</Typography>
                <Typography sx={{ fontSize: 13, color: t.colors.textMuted }}>{row.who}</Typography>
              </Box>
            ))}
          </Box>
        );
      default:
        return null;
    }
  })();

  const titles = {
    raci: "RACI & role",
    timeline: "Timeline",
    messaging: "Messaging",
    platform_access: "Platform access",
    projects_tasks: "Projects & tasks",
    permission_groups: "Permission groups",
    audit: "Audit",
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={typeof window !== "undefined" && window.innerWidth < 600}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: `${t.radius.lg}px` },
          boxShadow: t.shadow.modal,
          fontFamily: t.font.family,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, fontFamily: t.font.family, pb: 0.5 }}>
        {titles[section] || "Wireframe"}
        <Chip
          label="Wireframe"
          size="small"
          sx={{
            ml: 1,
            height: 22,
            fontSize: 11,
            fontWeight: 600,
            background: t.colors.navSoonBg,
          }}
        />
      </DialogTitle>
      <DialogContent>
        <WireframeBanner />
        {body}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Close
        </Button>
        <Button disabled variant="contained" sx={{ textTransform: "none" }}>
          Save (disabled)
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MemberModal({ open, onClose, member, canEdit, onMemberUpdated }) {
  const [contactOpen, setContactOpen] = useState(false);
  const [wireSection, setWireSection] = useState("");
  const name = member
    ? `${member.first_name || ""} ${member.last_name || ""}`.trim()
    : "";

  if (!member) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        fullScreen={typeof window !== "undefined" && window.innerWidth < 600}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: `${t.radius.lg}px` },
            boxShadow: t.shadow.modal,
            fontFamily: t.font.family,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontFamily: t.font.family, pb: 0.5 }}>
          {name || "Team member"}
          {member.is_test_account && (
            <Chip
              label="Test account"
              size="small"
              sx={{
                ml: 1,
                height: 22,
                fontSize: 11,
                fontWeight: 600,
                background: t.colors.warningSoft,
              }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: t.colors.textMuted, mb: 2 }}>
            {member.email}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1.5,
            }}
          >
            <SectionTile title="Contact" status="Live" onClick={() => setContactOpen(true)} />
            <SectionTile
              title="RACI & role"
              status="Wireframe"
              onClick={() => setWireSection("raci")}
            />
            <SectionTile
              title="Timeline"
              status="Wireframe"
              onClick={() => setWireSection("timeline")}
            />
            <SectionTile
              title="Messaging"
              status="Wireframe"
              onClick={() => setWireSection("messaging")}
            />
            <SectionTile
              title="Platform access"
              status="Wireframe"
              onClick={() => setWireSection("platform_access")}
            />
            <SectionTile
              title="Projects & tasks"
              status="Wireframe"
              onClick={() => setWireSection("projects_tasks")}
            />
            <SectionTile
              title="Permission groups"
              status="Wireframe"
              onClick={() => setWireSection("permission_groups")}
            />
            <SectionTile
              title="Audit"
              status="Wireframe"
              onClick={() => setWireSection("audit")}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ContactDialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        member={member}
        canEdit={canEdit}
        onSaved={(updated) => {
          onMemberUpdated(updated);
        }}
      />
      <WireframeDialog
        open={Boolean(wireSection)}
        onClose={() => setWireSection("")}
        section={wireSection}
        member={member}
      />
    </>
  );
}

export default function UsersAccessPage() {
  const [users, setUsers] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${getApiBase()}/api/admin/v2/users`, {
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Load failed (${res.status})`);
      const list = (data.users || []).map((u) => ({
        ...u,
        is_test_account:
          typeof u.is_test_account === "boolean"
            ? u.is_test_account
            : String(u.email || "")
                .toLowerCase()
                .endsWith("@test.autoshop.com"),
      }));
      setUsers(list);
      setCanEdit(Boolean(data.can_edit_contact));
    } catch (e) {
      setError(e.message || "Failed to load team");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const hay = `${u.first_name} ${u.last_name} ${u.email} ${u.phone_number}`.toLowerCase();
      return hay.includes(q);
    });
  }, [users, query]);

  const teamMembers = useMemo(
    () => filtered.filter((u) => !u.is_test_account),
    [filtered],
  );
  const testAccounts = useMemo(
    () => filtered.filter((u) => u.is_test_account),
    [filtered],
  );

  const missingPhoneTeam = teamMembers.filter(
    (u) => !String(u.phone_number || "").trim(),
  ).length;

  const tileGridSx = {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "1fr 1fr",
      md: "1fr 1fr 1fr",
    },
    gap: 1.5,
  };

  return (
    <Box>
      <Typography sx={{ fontSize: { xs: 20, sm: 22 }, fontWeight: 700, mb: 0.5 }}>
        Users & Access
      </Typography>
      <Typography sx={{ color: t.colors.textMuted, fontSize: 14, mb: 2.5, maxWidth: 560 }}>
        Platform team contacts. Login email is the invite identity for Supabase, GitHub, and Expo.
        Accounts on <strong>@test.autoshop.com</strong> are listed separately.
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mb: 2.5 }}>
        <SummaryChip label="Platform team" value={loading ? "…" : String(teamMembers.length)} />
        <SummaryChip label="Test accounts" value={loading ? "…" : String(testAccounts.length)} />
        <SummaryChip label="Missing phone (team)" value={loading ? "…" : String(missingPhoneTeam)} />
        <SummaryChip label="Supabase invites" value="—" />
      </Box>

      <TextField
        size="small"
        placeholder="Search name or email"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{ mb: 2.5, maxWidth: 360, width: "100%", background: t.colors.surface }}
      />

      {loading && (
        <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <>
          <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 1.25 }}>
            Platform team
          </Typography>
          {teamMembers.length === 0 ? (
            <Typography sx={{ color: t.colors.textMuted, mb: 3 }}>
              No platform team members match.
            </Typography>
          ) : (
            <Box sx={{ ...tileGridSx, mb: 3.5 }}>
              {teamMembers.map((m) => (
                <MemberTile key={m.user_id} member={m} onClick={() => setSelected(m)} />
              ))}
            </Box>
          )}

          <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 0.5 }}>
            Test accounts
          </Typography>
          <Typography sx={{ color: t.colors.textMuted, fontSize: 13, mb: 1.25 }}>
            Emails ending in @test.autoshop.com (QA / automation) — not used for Supabase org invites.
          </Typography>
          {testAccounts.length === 0 ? (
            <Typography sx={{ color: t.colors.textMuted }}>
              No test accounts match.
            </Typography>
          ) : (
            <Box sx={tileGridSx}>
              {testAccounts.map((m) => (
                <MemberTile key={m.user_id} member={m} onClick={() => setSelected(m)} />
              ))}
            </Box>
          )}
        </>
      )}

      <MemberModal
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        member={selected}
        canEdit={canEdit}
        onMemberUpdated={(updated) => {
          const withFlag = {
            ...updated,
            is_test_account: String(updated.email || "")
              .toLowerCase()
              .endsWith("@test.autoshop.com"),
          };
          setSelected(withFlag);
          setUsers((prev) =>
            prev.map((u) => (u.user_id === withFlag.user_id ? { ...u, ...withFlag } : u)),
          );
        }}
      />
    </Box>
  );
}
